/**
 * Send-refusal codes that mean «the letter is already on its way», NOT «the send
 * failed». Today one member — `ReplySendPg` raises `duplicate_send_pending` when
 * an identical send for the same idempotency key is still inside the reconcile
 * window — but the CATEGORY is what callers branch on, so a future benign refusal
 * only has to be added here.
 */
const SEND_ALREADY_IN_FLIGHT_CODES = new Set(['duplicate_send_pending']);
/**
 * Part of the {@link ReplyPort} CONTRACT, not of any one adapter: did the send
 * refuse because this letter is ALREADY IN FLIGHT?
 *
 * The distinction is the one that matters to a node whose next move depends on
 * whether an answer is still coming: nothing was lost, the letter is or will be
 * out, so a wait remains legitimate. Every other refusal is a genuine failure.
 *
 * Duck-typed on purpose. The concrete `ReplyFailedError` lives in
 * `@wit-support/admin-core`, an ADAPTER-side package the pipeline core must not
 * import (hexagonal rule). The shape — a `code` from
 * {@link SEND_ALREADY_IN_FLIGHT_CODES} — is the contract; the `name` check keeps
 * an unrelated error that happens to carry a `code` field from matching.
 */
export const isSendAlreadyInFlight = (err) => {
    if (typeof err !== 'object' || err === null)
        return false;
    const e = err;
    return (e.name === 'ReplyFailedError' &&
        typeof e.code === 'string' &&
        SEND_ALREADY_IN_FLIGHT_CODES.has(e.code));
};
/**
 * The profile a `field_match(billing)` sees when the worker put none on the
 * context — an OLD RunContext (manual path, existing tests) or a delivery where
 * billing could not be loaded. Everything unknown, nobody a whale. See
 * {@link BillingProfile} for why this is a fallback and not a throw.
 */
export const UNKNOWN_BILLING_PROFILE = Object.freeze({
    succeededChargesCount: null,
    isWhale: false,
});
//# sourceMappingURL=ports.js.map